import { inject, Injectable } from '@angular/core';
import {
    FiOnboardingReportDateType,
    FiOnboardingReportGroupType,
    FiOnboardingReportType,
    FiOnboardingReportWidgetType,
} from '@fi-app/fi-onboarding/fi-onboarding-report/fi-onboarding-report-enums';
import {
    IOnboardingPeriodDateRange,
    IOnboardingReportParameter,
    IOnboardingReportWidget,
    IOnboardingReportWidgetFilter,
} from '@fi-app/fi-onboarding/fi-onboarding-report/fi-onboarding-report';
import { cloneDeep, isDefined } from '@fi-shared/utils';
import { INameIdObj } from '@fi-shared/fi-shared-interfaces';
import { AppTextService } from '@fi-shared/app-text';
import { DeepReadonly } from '@fi-app/shared/types/deep-readonly';
import { coerceNumberProperty } from '@angular/cdk/coercion';
import { FiStateService } from '@fi-shared/fi-state-proxy/fi-state.proxy.service';
import { RawParams } from '@uirouter/angular';
import { Subject } from 'rxjs';
import { FiDialogsService } from '@fi-shared/fi-dialogs/fi-dialogs.service';
import { FiOnboardingReportTableFilterHelperService } from '@fi-app/fi-onboarding/fi-onboarding-report/shared/fi-onboarding-report-table-filter-helper.service';
import { FiFilterType } from '@fi-shared/fi-items-list/fi-items-list-filter/fi-filter-type-enum';
import { FiOnboardingReportNewWidgetDialogComponent } from '../fi-onboarding-report-dashboard/fi-onboarding-report-new-widget-dialog/fi-onboarding-report-new-widget-dialog.component';
import { FiReportWidgetDialogsService } from '@fi-shared/fi-report-widget/fi-report-widget-dialogs.service';
import { FiRecentActivityWidgetDialogComponent } from '../fi-onboarding-report-dashboard/fi-onboarding-report-widget/recent-activity-widget/recent-activity-widget-dialog/recent-activity-widget-dialog.component';
import { TSortOrder } from '@fi-shared/fi-items-list/fi-infinite-scroll-data-source';
import { FiOnboardingStatusType } from '@fi-app/fi-onboarding/fi-onboarding-enums';

type TDefaultWidgetNames = Partial<Record<FiOnboardingReportType, string>>;
type TDefaultWidgetDescriptions = Partial<Record<FiOnboardingReportType, string>>;
type TWidgetIconClasses = Partial<Record<FiOnboardingReportType, string>>;

@Injectable()
export class FiOnboardingReportHelperService {
    private readonly appTextService = inject(AppTextService);
    private readonly fiStateService = inject(FiStateService);
    private readonly fiDialogsService = inject(FiDialogsService);
    private readonly fiReportWidgetDialogsService = inject(FiReportWidgetDialogsService);
    private readonly fiOnboardingReportTableFilterHelperService = inject(FiOnboardingReportTableFilterHelperService);

    static readonly STATUS_SORT_COLUMN_NAME = 'Status';

    private lastReportWidgetType: FiOnboardingReportWidgetType;
    private savedFilters: {
        [reportWidgetType: number]: IOnboardingReportWidgetFilter;
    } = {};

    readonly onboardingReportDateOptions: DeepReadonly<INameIdObj[]> = [
        {
            Id: FiOnboardingReportDateType.PastWeek,
            Name: this.appTextService.get('common.last_week'),
        },
        {
            Id: FiOnboardingReportDateType.PastMonth,
            Name: this.appTextService.get('common.last_month'),
        },
        {
            Id: FiOnboardingReportDateType.PastQuarter,
            Name: this.appTextService.get('common.last_quarter'),
        },
        {
            Id: FiOnboardingReportDateType.PastYear,
            Name: this.appTextService.get('common.last_year'),
        },
    ];

    readonly defaultWidgetNames: DeepReadonly<TDefaultWidgetNames> = {
        [FiOnboardingReportType.Total]: this.appTextService.get('onboarding.report_total_onboardings'),
        [FiOnboardingReportType.Synchronised]: this.appTextService.get('onboarding.report_synchronised_onboardings'),
        [FiOnboardingReportType.Independent]: this.appTextService.get('onboarding.report_independent_onboardings'),
        [FiOnboardingReportType.ParticipantsOnboarded]: this.appTextService.get(
            'onboarding.report_participants_onboarded',
        ),
    };

    readonly defaultWidgetDescriptions: DeepReadonly<TDefaultWidgetDescriptions> = {
        [FiOnboardingReportType.Total]: this.appTextService.get('onboarding.report_total_onboardings_description'),
        [FiOnboardingReportType.Synchronised]: this.appTextService.get(
            'onboarding.report_synchronised_onboardings_description',
        ),
        [FiOnboardingReportType.Independent]: this.appTextService.get(
            'onboarding.report_independent_onboardings_description',
        ),
        [FiOnboardingReportType.ParticipantsOnboarded]: this.appTextService.get(
            'onboarding.report_participants_onboarded_description',
        ),
    };
    readonly widgetIconClasses: DeepReadonly<TWidgetIconClasses> = {
        [FiOnboardingReportType.Total]: 'far fa-file-certificate',
        [FiOnboardingReportType.Synchronised]: 'far fa-merge',
        [FiOnboardingReportType.Independent]: 'far fa-swap-arrows',
        [FiOnboardingReportType.ParticipantsOnboarded]: 'fal fa-award',
    };

    // todo: uncomment after implementing all widgets
    readonly availableOnboardingReportWidgets: { IconClassName: string; Name: string; Id: FiOnboardingReportType }[] = [
        {
            IconClassName: this.widgetIconClasses[FiOnboardingReportType.Total],
            Name: this.defaultWidgetNames[FiOnboardingReportType.Total],
            Id: FiOnboardingReportType.Total,
        },
        // {
        //     IconClassName: this.widgetIconClasses[FiOnboardingReportType.Synchronised],
        //     Name: this.appTextService.get('onboarding.synchronized'),
        //     Id: FiOnboardingReportType.Synchronised,
        // },
        // {
        //     IconClassName: this.widgetIconClasses[FiOnboardingReportType.Independent],
        //     Name: this.appTextService.get('onboarding.independent'),
        //     Id: FiOnboardingReportType.Independent,
        // },
        {
            IconClassName: this.widgetIconClasses[FiOnboardingReportType.ParticipantsOnboarded],
            Name: this.appTextService.get('common.completed_plural'),
            Id: FiOnboardingReportType.ParticipantsOnboarded,
        },
    ];

    getReportName(widget: IOnboardingReportWidget): string {
        if (!!widget.Name?.length) {
            return widget.Name;
        }
        return this.defaultWidgetNames[widget.ReportType] ?? '';
    }

    saveReportWidgetFilter(
        currentFilter: IOnboardingReportWidgetFilter,
        reportWidgetType: FiOnboardingReportWidgetType,
    ) {
        this.savedFilters[reportWidgetType] = currentFilter;
    }

    getReportWidgetFilterFromState(
        widget: IOnboardingReportWidget,
        args?: {
            stateParams?: RawParams;
            reportWidgetType?: FiOnboardingReportWidgetType;
        },
    ): IOnboardingReportWidgetFilter {
        if (!isDefined(args)) {
            args = {};
        }
        if (!isDefined(args.stateParams)) {
            args.stateParams = this.fiStateService.params;
        }

        const reportWidgetType = args.reportWidgetType;
        const onboardingId = coerceNumberProperty(args.stateParams.onboardingId);
        const businessUnitId = coerceNumberProperty(args.stateParams.businessUnitId);
        const userId = coerceNumberProperty(args.stateParams.userId);
        let filter = this.getReportWidgetFilter(widget, {
            onboardingId: onboardingId,
            businessUnitId: businessUnitId,
            userId: userId,
        });

        // back button click, restore previously saved filters
        const shouldRestoreSavedFilter =
            (reportWidgetType === FiOnboardingReportWidgetType.OnboardingCompletion &&
                this.lastReportWidgetType === FiOnboardingReportWidgetType.OnboardingLocations) ||
            (reportWidgetType === FiOnboardingReportWidgetType.OnboardingLocations &&
                this.lastReportWidgetType === FiOnboardingReportWidgetType.OnboardingParticipants);

        if (shouldRestoreSavedFilter) {
            if (isDefined(this.savedFilters[reportWidgetType])) {
                filter = this.savedFilters[reportWidgetType];
            }
        } else {
            if (
                reportWidgetType === FiOnboardingReportWidgetType.OnboardingCompletion ||
                reportWidgetType === FiOnboardingReportWidgetType.ParticipantsOnboarded
            ) {
                // clear saved filters
                this.savedFilters = {};
            } else {
                let lastSavedFilter = this.savedFilters[this.lastReportWidgetType];
                if (isDefined(lastSavedFilter)) {
                    if (
                        this.fiOnboardingReportTableFilterHelperService.isFilterMenuAvailable(
                            FiFilterType.BusinessUnit,
                            widget.ReportType,
                            args.reportWidgetType,
                        )
                    ) {
                        if (!businessUnitId) {
                            filter.BusinessUnitIds = lastSavedFilter.BusinessUnitIds;
                        }
                    } else {
                        delete filter.BusinessUnitIds;
                    }
                    if (
                        this.fiOnboardingReportTableFilterHelperService.isFilterMenuAvailable(
                            FiFilterType.Area,
                            widget.ReportType,
                            args.reportWidgetType,
                        )
                    ) {
                        filter.AreaIds = lastSavedFilter.AreaIds;
                    } else {
                        delete filter.AreaIds;
                    }
                    if (
                        this.fiOnboardingReportTableFilterHelperService.isFilterMenuAvailable(
                            FiFilterType.BusinessArea,
                            widget.ReportType,
                            args.reportWidgetType,
                        )
                    ) {
                        filter.BusinessAreaIds = lastSavedFilter.BusinessAreaIds;
                    } else {
                        delete filter.BusinessAreaIds;
                    }
                }
            }
        }

        this.lastReportWidgetType = args.reportWidgetType;
        this.saveReportWidgetFilter(filter, args.reportWidgetType);

        return filter;
    }

    getReportWidgetFilter(
        widget: IOnboardingReportWidget,
        args?: {
            onboardingId?: number;
            businessUnitId?: number;
            userId?: number;
        },
    ): IOnboardingReportWidgetFilter {
        let currentFilter: IOnboardingReportWidgetFilter = {};
        if (widget.Settings.CurrentFilterGuid) {
            currentFilter = cloneDeep(
                widget.Settings.Filters.find((item) => item.Guid === widget.Settings.CurrentFilterGuid),
            );
        }
        if (args?.onboardingId) {
            currentFilter.OnboardingIds = [args.onboardingId];
        }
        if (args?.businessUnitId) {
            currentFilter.BusinessUnitIds = [args.businessUnitId];
        }
        if (args?.userId) {
            currentFilter.UserIds = [args.userId];
        }
        return currentFilter;
    }

    getReportGroup(reportType: FiOnboardingReportType): FiOnboardingReportGroupType {
        if (
            [
                FiOnboardingReportType.Total,
                FiOnboardingReportType.Independent,
                FiOnboardingReportType.Synchronised,
            ].includes(reportType)
        )
            return FiOnboardingReportGroupType.Onboardings;

        if ([FiOnboardingReportType.ParticipantsOnboarded].includes(reportType))
            return FiOnboardingReportGroupType.ParticipantsOnboarded;

        if ([FiOnboardingReportType.RecentActivities].includes(reportType))
            return FiOnboardingReportGroupType.RecentActivities;

        throw new Error(`unknown reportType item in getReportGroup ${reportType}`);
    }

    getReportWidgetTypeForDetails(widget: IOnboardingReportWidget) {
        const reportGroup = this.getReportGroup(widget.ReportType);
        if (reportGroup === FiOnboardingReportGroupType.Onboardings) {
            return FiOnboardingReportWidgetType.OnboardingCompletion;
        }
        if (reportGroup === FiOnboardingReportGroupType.ParticipantsOnboarded) {
            return FiOnboardingReportWidgetType.ParticipantsOnboarded;
        }
        if (reportGroup === FiOnboardingReportGroupType.RecentActivities) {
            return FiOnboardingReportWidgetType.RecentActivities;
        }
        throw new Error(`getReportWidgetType: unknown widget report type ${widget.ReportType}`);
    }

    getReportParameter(args: IReportParameterUpdateArgs): IOnboardingReportParameter {
        const parameter = {} as IOnboardingReportParameter;
        this.updateReportParameter(parameter, args);
        return parameter;
    }

    updateReportParameter(parameter: IOnboardingReportParameter, args: IReportParameterUpdateArgs) {
        parameter.WidgetId = args.widget.Id;
        parameter.ReportType = args.widget.ReportType;
        parameter.Filter = args.filter;
        parameter.LoadParticipantsSummary = args.loadParticipantsSummary;
        parameter.LoadLocationsSummary = args.loadLocationsSummary;
        parameter.LoadOnboardings = args.loadOnboardings;
        parameter.LoadOnboardingLocations = args.loadOnboardingLocations;
        parameter.LoadOnboardingParticipants = args.loadOnboardingParticipants;
        parameter.DateRange = args.dateRange;
    }

    isFilterEmpty(filter: IOnboardingReportWidgetFilter): boolean {
        return (
            !isDefined(filter) ||
            (!filter.OnboardingIds?.length &&
                !filter.OnboardingCategories?.length &&
                !filter.BusinessUnitIds?.length &&
                !filter.AreaIds?.length &&
                !filter.BusinessAreaIds?.length &&
                !filter.UserIds?.length)
        );
    }

    private _openAddReportWidgetDialog$ = new Subject<void>();
    triggerOpenAddReportWidgetDialog(): void {
        this._openAddReportWidgetDialog$.next();
    }
    openAddReportWidgetDialog$ = this._openAddReportWidgetDialog$.asObservable();
    openAddReportWidgetDialog() {
        this.fiReportWidgetDialogsService
            .getNewWidgetTypeSelectDialog(this.availableOnboardingReportWidgets, true)
            .onYesButtonClick((reportType) => {
                this.openWidgetSettingsDialog(reportType);
            });
    }

    private _saveReportWidget$ = new Subject<IOnboardingReportWidget>();
    saveReportWidget$ = this._saveReportWidget$.asObservable();

    private openWidgetSettingsDialog(reportType: FiOnboardingReportType) {
        return this.fiDialogsService
            .custom({
                contentComponentRef: FiOnboardingReportNewWidgetDialogComponent,
                contentComponentData: reportType,
                hideHeader: true,
                hideFooter: true,
                hideDialogTopBorder: true,
                revertHeightChanges: true,
                config: {
                    panelClass: ['fi-rounded-dialog-panel', 'fi-new-widget-dialog-panel'],
                    autoFocus: false,
                    maxWidth: '90%',
                    minWidth: '500px',
                    width: '1124px',
                    restoreFocus: false,
                },
            })
            .onYesButtonClick((result) => {
                if (result.openTypeSelectStep) {
                    this.openAddReportWidgetDialog();
                    return;
                }
                this._saveReportWidget$.next(result.widget);
            });
    }

    openRecentActivityDialog(widget: IOnboardingReportWidget) {
        return this.fiDialogsService.custom({
            contentComponentRef: FiRecentActivityWidgetDialogComponent,
            contentComponentData: widget,
            hideHeader: true,
            hideFooter: true,
            hideDialogTopBorder: true,
            config: {
                panelClass: ['fi-rounded-dialog-panel', 'fi-dialog-content-no-bottom-padding'],
                width: '760px',
                maxWidth: '90%',
                autoFocus: false,
                restoreFocus: false,
            },
        });
    }

    getStatusSortOrderFn<T extends { Status: FiOnboardingStatusType }>(
        sortColumn: string,
        sortDirection: boolean,
        defaultSortOrderFn: TSortOrder<T>,
    ): TSortOrder<T> {
        if (sortColumn === FiOnboardingReportHelperService.STATUS_SORT_COLUMN_NAME) {
            return (item) => {
                if (item.Status === FiOnboardingStatusType.NotAvailable) {
                    return sortDirection ? -1 : Number.MAX_SAFE_INTEGER;
                }
                return item.Status;
            };
        }
        return defaultSortOrderFn;
    }
}

interface IReportParameterUpdateArgs {
    widget: IOnboardingReportWidget;
    filter: IOnboardingReportWidgetFilter;
    loadParticipantsSummary?: boolean;
    loadLocationsSummary?: boolean;
    loadOnboardings?: boolean;
    loadOnboardingLocations?: boolean;
    loadOnboardingParticipants?: boolean;
    dateRange?: IOnboardingPeriodDateRange;
}
